import React, { useMemo } from 'react';
import { C, LAYOUT } from '../../shared/design-tokens.js';
import type { FleetVehicle } from './use-fleet-read-model.js';

interface RentalVehicle extends FleetVehicle {
  rental_expiration_date?: string;
  vendor?: string;
}

interface FleetRentalReadSeamProps {
  vehicles: RentalVehicle[];
}

function expirationLabel(dateStr: string | undefined) {
  if (!dateStr) return { text: '—', color: C.textMuted };
  const today = new Date();
  const exp = new Date(`${dateStr}T00:00:00`);
  const days = Math.floor((exp.getTime() - today.getTime()) / 86400000);
  if (days < 0)  return { text: `${dateStr} (expired)`,       color: C.red };
  if (days <= 7) return { text: `${dateStr} (${days}d left)`, color: C.red };
  if (days <= 30) return { text: `${dateStr} (${days}d left)`, color: C.gold };
  return { text: dateStr, color: C.text };
}

export default function FleetRentalReadSeam({ vehicles }: FleetRentalReadSeamProps) {
  const rentals = useMemo(() =>
    vehicles.filter((v: RentalVehicle) => v.lease_type === 'rental' || v.has_rental)
      .slice()
      .sort((a: RentalVehicle, b: RentalVehicle) => {
        const aDate = a.rental_expiration_date || a.expiration_date || '';
        const bDate = b.rental_expiration_date || b.expiration_date || '';
        return aDate.localeCompare(bDate);
      }),
    [vehicles]
  );

  if (!rentals.length) return null;

  return (
    <div style={LAYOUT.subPanel}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.gold }}>
          Rental status read seam
        </div>
        <div style={{ color: C.textMuted, fontSize: 12 }}>{rentals.length} rental{rentals.length !== 1 ? 's' : ''}</div>
      </div>

      <div style={{ display: 'grid', gap: 10 }}>
        {rentals.map((vehicle: RentalVehicle) => {
          const expDate = vehicle.rental_expiration_date || vehicle.expiration_date;
          const exp = expirationLabel(expDate);
          const vendor = vehicle.rental_vendor || vehicle.vendor || '—';
          return (
            <div key={vehicle.id} style={{ padding: 12, borderRadius: 10, background: C.panel, border: `1px solid ${C.borderSoft}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 6 }}>
                <div style={{ color: C.text, fontSize: 14, fontWeight: 700 }}>#{vehicle.truck_number || '—'}</div>
                <div style={{ color: C.textMuted, fontSize: 12 }}>{vehicle.terminal_name || vehicle.current_terminal_name || '—'}</div>
              </div>
              <div style={{ display: 'grid', gap: 4, gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}>
                <div style={{ color: C.textMuted, fontSize: 12 }}>Vendor: <span style={{ color: C.text }}>{vendor}</span></div>
                <div style={{ color: C.textMuted, fontSize: 12 }}>Expires: <span style={{ color: exp.color, fontWeight: exp.color === C.red ? 700 : 400 }}>{exp.text}</span></div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
